import React, { useState, useEffect } from 'react';
import Truncate from 'react-truncate';
import {
  Input,
  Table,
  Tooltip as TooltipUIKit
} from 'syngenta-digital-cropwise-react-ui-kit';

import CropIconMapping from 'components/CropIconMapping';
import FieldIcon from 'components/Icons/FieldIcon';
import LayoutConstants from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutConstants';
import { CropZone } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import cleanGeometry from 'helpers/cleanGeometry';
import { Maths } from 'helpers/maths';
import alphanumSort from 'helpers/sorters';
import { getHeaderSortClassName } from 'helpers/tableSort';
import {
  getDefaultAreaType,
  AreaType
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/areaTypeConstants';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import TableActions from '../../../TableActions';
import CropZoneEditModal from '../CropZoneEditModal';
import {
  calculateCoveragePercent,
  calculateAppliedAcreage,
  calculateTotalAppliedAcreage
} from '../../helpers/cropZoneMath';
import './index.css';

const NUMBER_INPUT_LOWER_LIMIT = 0;
const PAGE_SIZE = 20;

const sortByFieldThenZone = (
  data: CropZone[],
  order: 'asc' | 'desc' = 'asc'
) => {
  const sortedByZone = alphanumSort([...data], 'name', order);
  return alphanumSort(sortedByZone, 'fieldName', order);
};

const Tooltip: React.FC<{
  title: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  children: React.ReactNode;
}> = ({ title, placement = 'top', children }) => {
  return (
    <TooltipUIKit
      title={title}
      placement={placement}
      color="#232630"
      align={{ offset: [0, 6] }}
    >
      {children}
    </TooltipUIKit>
  );
};

const ArchivedTag: React.FC = () => (
  <span style={{ whiteSpace: 'nowrap' }}>
    {' '}
    {LayoutStrings.cropZones.archived}
  </span>
);

const PropertyNameCell: React.FC<{ record: CropZone }> = ({ record }) => {
  const [isTruncated, setIsTruncated] = useState(false);
  return (
    <div className="max-w-full">
      <Tooltip title={isTruncated ? record.propertyName : ''} placement="top">
        <Truncate
          lines={2}
          ellipsis={
            <span>…{record.isPropertyArchived && <ArchivedTag />}</span>
          }
          onTruncate={setIsTruncated}
        >
          {record.propertyName}
          {!isTruncated && record.isPropertyArchived && <ArchivedTag />}
        </Truncate>
      </Tooltip>
    </div>
  );
};

const CropZoneNameCell: React.FC<{ record: CropZone }> = ({ record }) => {
  const [isFieldTruncated, setIsFieldTruncated] = useState(false);
  const [isCropZoneTruncated, setIsCropZoneTruncated] = useState(false);
  return (
    <div className="py-6px">
      <div className="text-neutral-60 max-w-full">
        <Tooltip
          title={isFieldTruncated ? record.fieldName : ''}
          placement="top"
        >
          <Truncate
            lines={1}
            ellipsis={
              <span title={record.fieldName}>
                …{record.isFieldArchived && <ArchivedTag />}
              </span>
            }
            onTruncate={setIsFieldTruncated}
          >
            {record.fieldName}
            {!isFieldTruncated && record.isFieldArchived && <ArchivedTag />}
          </Truncate>
        </Tooltip>
      </div>
      <div className="max-w-full">
        <Tooltip title={isCropZoneTruncated ? record.name : ''} placement="top">
          <Truncate
            ellipsis={<span title={record.name}>…</span>}
            onTruncate={setIsCropZoneTruncated}
          >
            {record.name}
          </Truncate>
        </Tooltip>
      </div>
    </div>
  );
};

const CropCell: React.FC<{ record: CropZone }> = ({ record }) => {
  const [isTruncated, setIsTruncated] = useState(false);
  if (!record.crop) {
    return <div />;
  }
  return (
    <div className="items-start flex">
      <div style={{ flex: '0 0 auto' }} className="mr-1">
        <CropIconMapping cropObject={record.crop} size={18} />
      </div>
      <div
        className="flex items-center min-w-0 whitespace-normal break-words"
        style={{
          flex: '1 1 0',
          minHeight: '18px'
        }}
      >
        <Tooltip title={isTruncated ? record.crop.name : ''} placement="top">
          <Truncate
            lines={2}
            ellipsis={<span title={record.crop.name}>…</span>}
            onTruncate={setIsTruncated}
          >
            {record.crop.name}
          </Truncate>
        </Tooltip>
      </div>
    </div>
  );
};

interface CropZonesTableProps {
  data: CropZone[];
  onTotalAcreageChange: (total: number) => void;
  onDataChange?: (data: CropZone[]) => void;
}

const CropZonesTable: React.FC<CropZonesTableProps> = ({
  data,
  onTotalAcreageChange,
  onDataChange
}) => {
  const [tableData, setTableData] = useState(data);
  const [focusedInput, setFocusedInput] = useState<{
    type: 'acreage' | 'coverage';
    idx: number;
  } | null>(null);
  const [pendingEdits, setPendingEdits] = useState<{ [key: string]: string }>(
    {}
  );
  const [sortInfo, setSortInfo] = useState<{
    columnKey: string;
    order: 'ascend' | 'descend' | null;
  }>({ columnKey: '', order: null });
  const [currentPage, setCurrentPage] = useState(1);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<{
    index: number;
    record: CropZone;
  } | null>(null);
  const [selectedAreaType, setSelectedAreaType] = useState<AreaType>(
    'reported'
  );
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  useEffect(() => {
    if (!data) return;
    // Use the new data from props (which includes updated coverage percentages from modal)
    // Only preserve non-coverage/acreage fields from existing tableData to maintain UI state
    const newTableData = data.map((newZone: CropZone) => {
      const existingZone = tableData.find(item => item.id === newZone.id);
      if (existingZone) {
        // Preserve UI-related fields but use new coverage/acreage values
        return {
          ...existingZone,
          ...newZone,
          // Always use the new coverage and acreage values from the updated data
          appliedAcreage: newZone.appliedAcreage,
          coveragePercent: newZone.coveragePercent
        };
      }
      return newZone;
    });
    setTableData(newTableData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Update a specific row in the table data
  const updateRow = (
    idx: number,
    changes: Partial<
      CropZone & { appliedAcreage: number; coveragePercent: number }
    >
  ) => {
    const currentRow = (tableData[idx] as unknown) as Record<string, unknown>;
    // Skip the entire cascade when no field actually changes. handleInputBlur
    // calls this with the existing value whenever the user clicks/tabs into
    // an input and out without typing — without this short-circuit, every
    // blur fires onDataChange + onTotalAcreageChange, which in turn cascade
    // through Formik validation and product recalculation. With many crop
    // zones (or products with tank info), that work blocks the blur event
    // handler and the next input visibly lags before it can take focus.
    const hasRealChange = (Object.entries(changes) as Array<
      [string, unknown]
    >).some(([key, value]) => currentRow[key] !== value);
    if (!hasRealChange) return;

    const updatedTableData = tableData.map((row, i) =>
      i === idx ? { ...row, ...changes } : row
    );
    setTableData(updatedTableData);

    // Notify parent component of the change so it can update Formik values
    onDataChange?.(updatedTableData);
    onTotalAcreageChange(calculateTotalAppliedAcreage(updatedTableData));
  };

  const getArea = (zone: CropZone) => {
    return zone.selectedAreaType === 'boundary'
      ? zone.boundaryArea!
      : zone.area;
  };

  // On acreage blur, recalculate coveragePercent
  const handleAcreageBlur = (value: number, idx: number) => {
    // If value is 0 or less, recalculate coveragePercent from existing value
    if (value <= NUMBER_INPUT_LOWER_LIMIT) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      handleCoverageBlur(tableData[idx].coveragePercent, idx);
      return;
    }

    const zone = tableData[idx];

    // Use helper function to calculate coverage percent
    const newCoverage = calculateCoveragePercent(value, getArea(zone));
    updateRow(idx, { appliedAcreage: value, coveragePercent: newCoverage });
  };

  // On coverage percent blur, recalculate appliedAcreage
  const handleCoverageBlur = (value: number, idx: number) => {
    // If value is 0 or less, recalculate appliedAcreage from existing value
    if (value <= NUMBER_INPUT_LOWER_LIMIT) {
      handleAcreageBlur(tableData[idx].appliedAcreage, idx);
      return;
    }

    const zone = tableData[idx];

    // Use helper function to calculate applied acreage
    const newAcreage = calculateAppliedAcreage(value, getArea(zone));
    updateRow(idx, { coveragePercent: value, appliedAcreage: newAcreage });
  };

  // Delete a row from the table data
  const deleteRow = (idx: number) => {
    const newTableData = tableData.filter((_, i) => i !== idx);
    setTableData(newTableData);

    // Notify parent component of the change so it can update Formik values
    onDataChange?.(newTableData);
    onTotalAcreageChange(calculateTotalAppliedAcreage(newTableData));
  };

  // Open edit modal for a specific row
  const openEditModal = (index: number, record: CropZone) => {
    setEditingRow({ index, record });
    setEditModalOpen(true);

    // Use the selectedAreaType from the crop zone data if available,
    // otherwise fallback to default based on reported area
    const areaType =
      record.selectedAreaType || getDefaultAreaType(record.reportedArea);
    setSelectedAreaType(areaType);
  };

  // Close edit modal
  const closeEditModal = () => {
    // Reset the selected area type to the saved value when cancelling
    if (editingRow) {
      const savedAreaType =
        editingRow.record.selectedAreaType ||
        getDefaultAreaType(editingRow.record.reportedArea);
      setSelectedAreaType(savedAreaType);
    }
    setEditModalOpen(false);
    setEditingRow(null);
  };

  // Handle area type selection change
  const handleAreaTypeChange = (value: 'reported' | 'boundary') => {
    setSelectedAreaType(value);
    // Don't save to cropZoneAreaTypes here - only save when user clicks Save
  };

  // Handle modal save action
  const handleModalSave = () => {
    if (editingRow) {
      const { reportedArea, boundaryArea } = editingRow.record;
      const newAreaValue =
        selectedAreaType === 'reported' ? reportedArea : boundaryArea;

      // Get current area value to check if it actually changes
      const currentRow = tableData[editingRow.index];

      // Only update the table data if the area value actually changes.
      // Compare against the row's current area (via getArea, which reflects the
      // currently-selected area type) — not appliedAcreage, since appliedAcreage
      // is area × coveragePercent / 100 and would only equal newAreaValue when
      // coveragePercent happens to be 100. Comparing to area correctly detects
      // a no-op modal save and avoids a spurious recalculation that would
      // introduce floating-point drift into appliedAcreage.
      if (newAreaValue === getArea(currentRow)) {
        // Even if area value doesn't change, we still need to save the selectedAreaType
        // to persist the user's choice across navigation
        const updatedTableData = [...tableData];
        updatedTableData[editingRow.index] = {
          ...updatedTableData[editingRow.index],
          selectedAreaType // Save the selected area type to persist across navigation
        };

        setTableData(updatedTableData);
        onDataChange?.(updatedTableData);
        // No total change needed — applied acreage did not change
      } else {
        // Update the table data with the selected area type and new area value
        const updatedTableData = [...tableData];
        const currentCoveragePercent =
          updatedTableData[editingRow.index].coveragePercent;

        // Calculate new applied acreage using existing coverage percent
        const newAppliedAcreage = Maths.divideSafeRound(
          Maths.multiplyRound(newAreaValue || 0, currentCoveragePercent),
          100
        );

        updatedTableData[editingRow.index] = {
          ...updatedTableData[editingRow.index],
          area: newAreaValue || 0,
          appliedAcreage: newAppliedAcreage,
          selectedAreaType // Save the selected area type to persist across navigation
        };

        setTableData(updatedTableData);
        onDataChange?.(updatedTableData);
        onTotalAcreageChange(calculateTotalAppliedAcreage(updatedTableData));
      }
    }
    closeEditModal();
  };

  const getDisplayValue = (
    value: number,
    type: 'acreage' | 'coverage',
    idx: number
  ) => {
    const key = `${type}-${idx}`;
    const isFocused = focusedInput?.type === type && focusedInput?.idx === idx;
    if (isFocused && pendingEdits[key] !== undefined) {
      return pendingEdits[key];
    }
    if (value !== undefined && value !== null) {
      return Number(value).toFixed(2);
    }
    return '';
  };

  const handleInputChange = (
    type: 'acreage' | 'coverage',
    idx: number,
    val: string
  ) => {
    setPendingEdits(prev => ({ ...prev, [`${type}-${idx}`]: val }));
  };

  const handleInputBlur = (
    type: 'acreage' | 'coverage',
    rowIdx: number,
    handler: (value: number, idx: number) => void
  ) => {
    const key = `${type}-${rowIdx}`;
    setFocusedInput(null);
    const val = Number(pendingEdits[key]);
    const originalValue =
      type === 'acreage'
        ? tableData[rowIdx].appliedAcreage
        : tableData[rowIdx].coveragePercent;

    // If the user clicked/tabbed into the input and left without changing the
    // value (or somehow blurred without a focus state), short-circuit. The
    // handler would otherwise recompute the dependent field (e.g. coverage
    // from acreage, or acreage from coverage) via floating-point math, which
    // can differ from the stored value by sub-decimal drift — that drift
    // would then be detected as a "real change" by updateRow and cascade
    // through to totalAppliedAcreage / products, silently rounding values
    // the user never touched.
    if (Number.isNaN(val) || val === originalValue) {
      setPendingEdits(prev => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
      return;
    }

    handler(val, rowIdx);
    setPendingEdits(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  // Sorting logic
  const getSortedData = () => {
    if (!sortInfo.order || !sortInfo.columnKey) return tableData;
    let sorted = [...tableData];
    if (sortInfo.columnKey === 'name') {
      sorted = sortByFieldThenZone(
        sorted,
        sortInfo.order === 'ascend' ? 'asc' : 'desc'
      );
    } else if (sortInfo.columnKey === 'cropName') {
      sorted = alphanumSort(
        sorted,
        'crop.name',
        sortInfo.order === 'ascend' ? 'asc' : 'desc'
      );
    } else if (sortInfo.columnKey === 'propertyName') {
      sorted = alphanumSort(
        sorted,
        'propertyName',
        sortInfo.order === 'ascend' ? 'asc' : 'desc'
      );
    }
    return sorted;
  };

  const handleTableChange = (_: any, __: any, sorter: any) => {
    setSortInfo({
      columnKey: sorter.columnKey,
      order: sorter.order
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const sortedData = getSortedData();

  const columns = [
    {
      render: (_: any, record: CropZone) => (
        <div className="pr-2px">
          <FieldIcon
            id={record.id}
            features={[
              {
                type: 'Feature',
                geometry: cleanGeometry(record.geometry),
                properties: {}
              }
            ]}
            border="#868CA2"
            color="#DFE2E7"
          />
        </div>
      ),
      width: 50
    },
    {
      title: `Crop Zones (${tableData.length})`,
      dataIndex: 'fieldName',
      key: 'name',
      sorter: (a: CropZone, b: CropZone) => {
        const sortedPair = sortByFieldThenZone([a, b], 'asc');
        return sortedPair[0] === a ? -1 : 1;
      },
      className: getHeaderSortClassName(
        'name',
        sortInfo.columnKey,
        sortInfo.order
      ),
      showSorterTooltip: false,
      sortOrder: sortInfo.columnKey === 'name' ? sortInfo.order : null,
      render: (_: any, record: CropZone) => <CropZoneNameCell record={record} />
    },
    {
      title: 'Crop',
      dataIndex: 'crop',
      key: 'cropName',
      sorter: (a: CropZone, b: CropZone) => alphanumSort([a, b], 'crop.name'),
      className: getHeaderSortClassName(
        'cropName',
        sortInfo.columnKey,
        sortInfo.order
      ),
      showSorterTooltip: false,
      sortOrder: sortInfo.columnKey === 'cropName' ? sortInfo.order : null,
      render: (_: any, record: CropZone) => <CropCell record={record} />
    },
    {
      title: 'Farm',
      dataIndex: 'propertyName',
      key: 'propertyName',
      sorter: (a: CropZone, b: CropZone) =>
        alphanumSort([a, b], 'propertyName'),
      className: getHeaderSortClassName(
        'propertyName',
        sortInfo.columnKey,
        sortInfo.order
      ),
      showSorterTooltip: false,
      sortOrder: sortInfo.columnKey === 'propertyName' ? sortInfo.order : null,
      render: (_: any, record: CropZone) => {
        return <PropertyNameCell record={record} />;
      }
    },
    {
      title: 'Applied Acreage',
      dataIndex: 'appliedAcreage',
      key: 'appliedAcreage',
      width: 158,
      render: (_: any, record: CropZone) => {
        // Find the original index in tableData for this record
        const originalIdx = tableData.findIndex(item => item.id === record.id);
        const displayValue = getDisplayValue(
          record.appliedAcreage,
          'acreage',
          originalIdx
        );
        return (
          <Input
            id={`appliedAcreage-${originalIdx}`}
            className="w-full"
            style={{ maxWidth: 'calc(100% - 16px)' }}
            value={displayValue}
            suffix={<span className="text-neutral-60">ac</span>}
            onFocus={() => {
              setFocusedInput({ type: 'acreage', idx: originalIdx });
              setPendingEdits(prev => ({
                ...prev,
                [`acreage-${originalIdx}`]:
                  record.appliedAcreage?.toString() ?? ''
              }));
            }}
            onChange={e => {
              handleInputChange('acreage', originalIdx, e.target.value);
            }}
            onBlur={() => {
              handleInputBlur('acreage', originalIdx, handleAcreageBlur);
            }}
          />
        );
      }
    },
    {
      title: 'Coverage Percent',
      dataIndex: 'coveragePercent',
      key: 'coveragePercent',
      width: 154,
      render: (_: any, record: CropZone) => {
        // Find the original index in tableData for this record
        const originalIdx = tableData.findIndex(item => item.id === record.id);
        const displayValue = getDisplayValue(
          record.coveragePercent,
          'coverage',
          originalIdx
        );
        return (
          <Input
            id={`coveragePercent-${originalIdx}`}
            className="w-full"
            style={{ maxWidth: 'calc(100% - 16px)' }}
            type="text"
            inputMode="decimal"
            value={displayValue}
            suffix={<span className="text-neutral-60">%</span>}
            onFocus={() => {
              setFocusedInput({ type: 'coverage', idx: originalIdx });
              setPendingEdits(prev => ({
                ...prev,
                [`coverage-${originalIdx}`]:
                  record.coveragePercent?.toString() ?? ''
              }));
            }}
            onChange={e => {
              handleInputChange('coverage', originalIdx, e.target.value);
            }}
            onBlur={() => {
              handleInputBlur('coverage', originalIdx, handleCoverageBlur);
            }}
          />
        );
      }
    },
    {
      title: '',
      key: 'actions',
      width: 107,
      render: (_: any, record: CropZone, idx: number) => {
        const { reportedArea } = record;
        const hasReportedArea =
          reportedArea !== undefined && reportedArea !== 0;

        return (
          <TableActions
            showEdit={hasReportedArea}
            onEdit={() => openEditModal(idx, record)}
            onDelete={() => deleteRow(idx)}
          />
        );
      }
    }
  ];

  return (
    <div className="application-crop-zones-table flex flex-col">
      <Table
        rowKey="id"
        dataSource={sortedData}
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total: sortedData.length,
          onChange: handlePageChange,
          showSizeChanger: false,
          position: ['bottomRight'],
          showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total}`
        }}
        onChange={handleTableChange}
        scroll={{ y: LayoutConstants.TABLE_VERTICAL_CROP_ZONES_SCROLL }}
      />

      <CropZoneEditModal
        open={editModalOpen}
        editingRow={editingRow}
        selectedAreaType={selectedAreaType}
        isDropdownVisible={isDropdownVisible}
        onAreaTypeChange={handleAreaTypeChange}
        onDropdownVisibleChange={setIsDropdownVisible}
        onCancel={closeEditModal}
        onSave={handleModalSave}
      />
    </div>
  );
};
export default CropZonesTable;
